import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { type DriveListSort, kDefaultDriveListSort } from '@/user_files/sorting'
import { FolderResponse } from '../schemas'

// ============================================================================
// Types
// ============================================================================

/**
 * Operation types for request tracking
 */
export type FolderOperationType = 'fetchRoot' | 'fetchFolder' | 'fetchChildren' | 'create' | 'rename' | 'delete' | 'move' | 'restore'

/**
 * Dialog types for folder CRUD operations
 */
export type FolderDialogType = 'create' | 'rename' | null

/**
 * Dialog state for folder operations
 */
export interface FolderDialogState {
  dialogType: FolderDialogType
  folderId: string | null // For rename — which folder
}

/**
 * Request state for a single async operation
 */
export interface FolderRequestState {
  operationType: FolderOperationType
  folderId?: string // Target folder (if applicable)
  status: 'pending' | 'success' | 'error'
  error?: string
  timestamp: number
}

export type FolderLoadRequestStatus = 'idle' | 'loading' | 'success' | 'error'

export interface LoadRequestState {
  status: FolderLoadRequestStatus
  error: string | null
  statusCode?: number
}

/**
 * Combined folder state: backend data + load status
 */
export interface FolderState {
  data: FolderResponse
  childrenLoaded: boolean // Have we fetched this folder's children?
  // Which sort produced the order in `childrenByParentId`. The backend owns row order, and this
  // cache is shared with consumers that request the default sort (the file picker, the move
  // dialog), so the drive has to be able to tell whether the order it holds is the one it asked for.
  childrenSort?: DriveListSort
  isLoading: boolean // Currently fetching this folder's data?
  childrenRequest: LoadRequestState
}

/**
 * URL initialization error state for a specific folder route.
 */
export interface UrlInitializationError {
  folderId: string
  error: string
  statusCode?: number
}

/**
 * Root state for folders feature
 */
export interface UserFoldersState {
  // Primary storage: normalized by folder ID
  folders: Record<string, FolderState>

  // Secondary indexes for efficient lookups
  childrenByParentId: Record<string, string[]> // parent_id -> [child_id, ...]
  rootByMatterId: Record<string, string> // matter_id -> root_folder_id
  rootFolderRequestsByMatterId: Record<string, LoadRequestState>

  // Navigation (single-layer UI)
  currentFolderId: string | null // Which folder we're viewing

  /**
   * Sort each matter's drive listing is displayed under, and the sort its list fetches ask for.
   * A matter with no entry has not been sorted this visit and uses the API default.
   *
   * This is drive UI state, but it cannot live in the drive: `UserFilesFolderRouteHandler` swaps
   * its children for a spinner until the URL folder and Redux agree, so every folder navigation
   * unmounts the drive subtree and would take a component-local sort with it. Keying by matter is
   * the design decision (Jennifer, Aug 26): the sort lasts the whole visit, and each matter keeps
   * its own rather than one sort following the user from matter to matter.
   */
  driveListSortByMatterId: Record<string, DriveListSort>

  // Dialog state
  dialog: FolderDialogState

  // Request tracking (for loading states, optimistic updates, etc.)
  activeRequests: Record<string, FolderRequestState> // requestId -> state

  // Global state
  initialLoadComplete: boolean

  // URL initialization state
  isInitializingFromUrl: boolean
  urlInitializationError: UrlInitializationError | null
}

const createIdleLoadRequestState = (): LoadRequestState => ({
  status: 'idle',
  error: null,
})

// ============================================================================
// Initial State
// ============================================================================

const initialState: UserFoldersState = {
  folders: {},
  childrenByParentId: {},
  rootByMatterId: {},
  rootFolderRequestsByMatterId: {},
  currentFolderId: null,
  driveListSortByMatterId: {},
  dialog: { dialogType: null, folderId: null },
  activeRequests: {},
  initialLoadComplete: false,
  isInitializingFromUrl: false,
  urlInitializationError: null,
}

// ============================================================================
// Slice
// ============================================================================

export const userFoldersSlice = createSlice({
  name: 'userFoldersState',
  initialState,
  reducers: {
    // ============== Listener Triggers ==============

    fetchRootFolder: (_state, _action: PayloadAction<{ matterId: string; force?: boolean }>) => {
      // Handled by listener
    },

    fetchRootFolderStarted: (state, action: PayloadAction<{ matterId: string }>) => {
      state.rootFolderRequestsByMatterId[action.payload.matterId] = {
        status: 'loading',
        error: null,
      }
    },

    fetchRootFolderSucceeded: (state, action: PayloadAction<{ matterId: string }>) => {
      state.rootFolderRequestsByMatterId[action.payload.matterId] = {
        status: 'success',
        error: null,
      }
    },

    fetchRootFolderFailed: (state, action: PayloadAction<{ matterId: string; error: string; statusCode?: number }>) => {
      state.rootFolderRequestsByMatterId[action.payload.matterId] = {
        status: 'error',
        error: action.payload.error,
        ...(action.payload.statusCode ? { statusCode: action.payload.statusCode } : {}),
      }
    },

    fetchFolderById: (_state, _action: PayloadAction<{ folderId: string }>) => {
      // Handled by listener
    },

    /** Refresh children; an omitted `sort` inherits the in-flight or cached order, then the default. */
    fetchFolderChildren: (_state, _action: PayloadAction<{ folderId: string; sort?: DriveListSort }>) => {
      // Handled by listener
    },

    restoreFolder: (_state, _action: PayloadAction<{ folderId: string }>) => {
      // Handled by listener
    },

    /**
     * Initialize folder view from URL parameter
     * The listener fetches the folder and ancestors; the drive hook loads its listings after mount.
     */
    initializeFromUrl: (state, _action: PayloadAction<{ folderId: string }>) => {
      state.isInitializingFromUrl = true
      state.urlInitializationError = null
    },

    /**
     * URL initialization completed successfully
     */
    urlInitializationComplete: (state) => {
      state.isInitializingFromUrl = false
      state.urlInitializationError = null
    },

    /**
     * URL initialization failed (invalid folder, network error, etc.)
     */
    urlInitializationFailed: (state, action: PayloadAction<UrlInitializationError>) => {
      state.isInitializingFromUrl = false
      state.urlInitializationError = action.payload
    },

    // ============== Request State ==============

    requestStarted: (
      state,
      action: PayloadAction<{
        requestId: string
        operationType: FolderOperationType
        folderId?: string
      }>
    ) => {
      const { requestId, operationType, folderId } = action.payload
      state.activeRequests[requestId] = {
        operationType,
        folderId,
        status: 'pending',
        timestamp: Date.now(),
      }

      // Set folder-level loading if applicable
      if (folderId && state.folders[folderId]) {
        state.folders[folderId].isLoading = true
        if (operationType === 'fetchChildren') {
          state.folders[folderId].childrenRequest = {
            status: 'loading',
            error: null,
          }
        }
      }
    },

    /** Retire a superseded request without changing the folder state owned by its replacement. */
    requestCancelled: (state, action: PayloadAction<{ requestId: string }>) => {
      delete state.activeRequests[action.payload.requestId]
    },

    requestSucceeded: (state, action: PayloadAction<{ requestId: string }>) => {
      const request = state.activeRequests[action.payload.requestId]
      if (request) {
        request.status = 'success'
        if (request.folderId && state.folders[request.folderId]) {
          state.folders[request.folderId].isLoading = false
          if (request.operationType === 'fetchChildren') {
            state.folders[request.folderId].childrenRequest = {
              status: 'success',
              error: null,
            }
          }
        }
      }
      delete state.activeRequests[action.payload.requestId]
    },

    requestFailed: (state, action: PayloadAction<{ requestId: string; error: string }>) => {
      const request = state.activeRequests[action.payload.requestId]
      if (request) {
        request.status = 'error'
        request.error = action.payload.error
        if (request.folderId && state.folders[request.folderId]) {
          state.folders[request.folderId].isLoading = false
          if (request.operationType === 'fetchChildren') {
            state.folders[request.folderId].childrenRequest = {
              status: 'error',
              error: action.payload.error,
            }
          }
        }
      }
    },

    // ============== Folder Data Updates ==============

    /**
     * Store a folder's own record, without claiming it a place in its parent's listing.
     *
     * A folder needs a record before anything can hold state about it — `requestStarted` and
     * `setFolderChildren` both skip a folder they have never seen, so navigating into one that is
     * missing leaves it loading forever. Membership is separate because the drive renders
     * `childrenByParentId` in the order it holds: on the folder it is displaying, only a listing
     * fetch may add a row, or it lands at the bottom and then moves.
     */
    upsertFolderRecord: (state, action: PayloadAction<FolderResponse>) => {
      const folder = action.payload
      const existingState = state.folders[folder.id]

      // Preserve load status if folder exists, otherwise initialize
      const childrenLoaded = existingState?.childrenLoaded ?? false
      const childrenSort = existingState?.childrenSort
      const isLoading = existingState?.isLoading ?? false
      const childrenRequest = existingState?.childrenRequest ?? createIdleLoadRequestState()

      state.folders[folder.id] = {
        data: folder,
        childrenLoaded,
        childrenSort,
        isLoading,
        childrenRequest,
      }

      // Update secondary indexes
      if (folder.is_root) {
        state.rootByMatterId[folder.matter_id] = folder.id
        state.rootFolderRequestsByMatterId[folder.matter_id] = {
          status: 'success',
          error: null,
        }
      }
    },

    /** Batch of `upsertFolderRecord`. */
    upsertFolderRecords: (state, action: PayloadAction<FolderResponse[]>) => {
      action.payload.forEach((folder) => {
        userFoldersSlice.caseReducers.upsertFolderRecord(state, {
          type: 'upsertFolderRecord',
          payload: folder,
        })
      })
    },

    /**
     * Upsert a single folder into state, and list it under its parent.
     * Updates secondary indexes automatically
     */
    upsertFolder: (state, action: PayloadAction<FolderResponse>) => {
      const folder = action.payload
      userFoldersSlice.caseReducers.upsertFolderRecord(state, { type: 'upsertFolderRecord', payload: folder })

      // Update parent's children list
      if (folder.parent_id) {
        const siblings = state.childrenByParentId[folder.parent_id] ?? []
        if (!siblings.includes(folder.id)) {
          state.childrenByParentId[folder.parent_id] = [...siblings, folder.id]
        }
      }
    },

    /**
     * Batch upsert folders (for children fetch)
     */
    upsertFolders: (state, action: PayloadAction<FolderResponse[]>) => {
      action.payload.forEach((folder) => {
        userFoldersSlice.caseReducers.upsertFolder(state, {
          type: 'upsertFolder',
          payload: folder,
        })
      })
    },

    /**
     * Set children for a parent folder (replaces existing children list)
     */
    setFolderChildren: (
      state,
      action: PayloadAction<{
        parentId: string
        children: FolderResponse[]
        sort?: DriveListSort
      }>
    ) => {
      const { parentId, children } = action.payload

      // Update children index
      state.childrenByParentId[parentId] = children.map((f) => f.id)

      // Upsert each child
      children.forEach((folder) => {
        userFoldersSlice.caseReducers.upsertFolder(state, {
          type: 'upsertFolder',
          payload: folder,
        })
      })

      // Mark children as loaded for parent
      if (state.folders[parentId]) {
        state.folders[parentId].childrenLoaded = true
        state.folders[parentId].childrenSort = action.payload.sort ?? kDefaultDriveListSort
        state.folders[parentId].childrenRequest = {
          status: 'success',
          error: null,
        }
      }
    },

    /**
     * Mark a folder's child list stale so the next visit refetches from the server.
     * We keep the existing rows around until that refetch happens.
     */
    invalidateFolderChildren: (state, action: PayloadAction<{ folderId: string }>) => {
      const folderState = state.folders[action.payload.folderId]
      if (!folderState) return

      folderState.childrenLoaded = false
      // `childrenSort` deliberately survives: this marks the list stale, not the ordering, and the
      // refetch it invites has to come back in the order the drive is currently displaying.
      folderState.childrenRequest = createIdleLoadRequestState()
    },

    clearFolderChildren: (state, action: PayloadAction<{ folderId: string }>) => {
      const folderState = state.folders[action.payload.folderId]
      delete state.childrenByParentId[action.payload.folderId]

      if (!folderState) return
      folderState.childrenLoaded = false
      folderState.childrenSort = undefined
      folderState.childrenRequest = createIdleLoadRequestState()
    },

    /**
     * Remove a folder from state (after delete)
     */
    removeFolder: (state, action: PayloadAction<{ folderId: string }>) => {
      const { folderId } = action.payload
      const folder = state.folders[folderId]
      if (!folder) return

      // Remove from parent's children list
      const parentId = folder.data.parent_id
      if (parentId && state.childrenByParentId[parentId]) {
        state.childrenByParentId[parentId] = state.childrenByParentId[parentId].filter((id) => id !== folderId)
      }

      // Remove from root index if applicable
      if (folder.data.is_root) {
        delete state.rootByMatterId[folder.data.matter_id]
        delete state.rootFolderRequestsByMatterId[folder.data.matter_id]
      }

      // Remove folder itself
      delete state.folders[folderId]

      // Clean up children index for this folder
      delete state.childrenByParentId[folderId]
    },

    /**
     * Move folder to new parent (update indexes)
     */
    moveFolderToParent: (
      state,
      action: PayloadAction<{
        folderId: string
        oldParentId: string | null
        newParentId: string
      }>
    ) => {
      const { folderId, oldParentId, newParentId } = action.payload

      // Remove from old parent's children
      if (oldParentId && state.childrenByParentId[oldParentId]) {
        state.childrenByParentId[oldParentId] = state.childrenByParentId[oldParentId].filter((id) => id !== folderId)
      }

      // Add to new parent's children
      const newSiblings = state.childrenByParentId[newParentId] ?? []
      if (!newSiblings.includes(folderId)) {
        state.childrenByParentId[newParentId] = [...newSiblings, folderId]
      }

      // Update folder's parent_id
      if (state.folders[folderId]) {
        state.folders[folderId].data.parent_id = newParentId
      }
    },

    // ============== Navigation ==============

    /**
     * Navigate into a folder (set as current)
     */
    setCurrentFolder: (state, action: PayloadAction<{ folderId: string | null }>) => {
      state.currentFolderId = action.payload.folderId
    },

    // ============== Drive Sort ==============

    /** Records the sort the user picked in the drive's column headers, for that matter's drive. */
    setDriveListSort: (state, action: PayloadAction<{ matterId: string; sort: DriveListSort }>) => {
      state.driveListSortByMatterId[action.payload.matterId] = action.payload.sort
    },

    // ============== Global State ==============

    setInitialLoadComplete: (state, action: PayloadAction<boolean>) => {
      state.initialLoadComplete = action.payload
    },

    clearUrlInitializationError: (state) => {
      state.urlInitializationError = null
    },

    /**
     * Clear all folder data for a matter (e.g., when switching matters)
     */
    clearMatterFolders: (state, action: PayloadAction<{ matterId: string }>) => {
      const { matterId } = action.payload

      // Find all folders for this matter and remove them
      Object.values(state.folders).forEach((folderState) => {
        if (folderState.data.matter_id === matterId) {
          userFoldersSlice.caseReducers.removeFolder(state, {
            type: 'removeFolder',
            payload: { folderId: folderState.data.id },
          })
        }
      })

      // Clear root index for this matter
      delete state.rootByMatterId[matterId]
      delete state.rootFolderRequestsByMatterId[matterId]
      // This runs when the matter is gone or no longer visible, not on a matter switch, so the
      // sort the user chose for it has nothing left to order.
      delete state.driveListSortByMatterId[matterId]

      // Clear current folder if it was in this matter
      if (state.currentFolderId && !state.folders[state.currentFolderId]) {
        state.currentFolderId = null
      }
    },

    /**
     * Reset to initial state
     */
    reset: () => initialState,

    // ============== Dialog State ==============

    /**
     * Open a folder dialog (create, rename)
     */
    openDialog: (state, action: PayloadAction<FolderDialogState>) => {
      state.dialog = action.payload
    },

    /**
     * Close the current dialog
     */
    closeDialog: (state) => {
      state.dialog = { dialogType: null, folderId: null }
    },
  },
})

// ============================================================================
// Exports
// ============================================================================

export const UserFoldersActions = userFoldersSlice.actions
export default userFoldersSlice.reducer
